// hooks/useApiQuery.ts
import { lastSetXTokenTimeStorage, store, xRbacTokenStorage } from '@/store'
import { moment } from '@seiue/moment'
import { Toast } from '@seiue/ui'
import * as sdk from 'packages/bi'
import { authApi } from 'packages/sdks-next/growp'
import { useQuery, UseQueryOptions } from 'react-query'

// 固定默认配置
const DEFAULT_QUERY_OPTIONS = {
  staleTime: 0,
  retry: false,
  refetchOnWindowFocus: false,
}

// SDK 方法类型
type SdkFunction = (args: any) => Promise<any>
type SdkMap = typeof sdk
export type SdkFunctionName = keyof SdkMap

// 提取 API 返回的 data 类型
type ApiData<TName extends SdkFunctionName> = Awaited<
  ReturnType<SdkMap[TName]>
> extends { data: infer D }
  ? D
  : never

interface UseApiQueryParams<TName extends SdkFunctionName> {
  apiName: TName // API 方法名（字符串）
  params?: Parameters<SdkMap[TName]>[0]
  disable?: boolean
  queryKey?: any[]
  options?: Partial<UseQueryOptions<ApiData<TName>, unknown>>
}

interface UseApiQueryReturn<TName extends SdkFunctionName> {
  data: ApiData<TName> | null
  loading: boolean
  reload: () => void
  refetching: boolean
}
let refreshingPromise: Promise<string | null> | null = null

export async function getValidToken(): Promise<string | null> {
  const token = store.get(xRbacTokenStorage)
  const lastTime = store.get(lastSetXTokenTimeStorage)
  const isExpired =
    !lastTime || moment().isAfter(moment(lastTime).add(1, 'hour'))

  if (token && !isExpired) return token

  if (!refreshingPromise) {
    refreshingPromise = (async () => {
      try {
        const { data: newToken } = await authApi.checkDataToken()
        if (newToken) {
          store.set(xRbacTokenStorage, newToken)
          store.set(lastSetXTokenTimeStorage, moment().toISOString())
        }
        return newToken
      } catch {
        return null
      } finally {
        refreshingPromise = null
      }
    })()
  }

  return refreshingPromise
}

/**
 * 使用 API 查询
 *
 * @param param0 - 参数
 * @param param0.apiName - API 方法名
 * @param param0.params - API 参数
 * @param param0.disable - 是否禁用
 * @param param0.queryKey - queryKey
 * @param param0.options - react-query 配置
 * @returns res
 */
export function useApiQuery<TName extends SdkFunctionName>({
  apiName,
  params = {} as Parameters<SdkMap[TName]>[0],
  disable,
  queryKey,
  options = {},
}: UseApiQueryParams<TName>): UseApiQueryReturn<TName> {
  const xRbacToken = store.get(xRbacTokenStorage)
  const enabled = !disable

  const apiFn = sdk[apiName] as SdkFunction
  if (typeof apiFn !== 'function') {
    throw new Error(`API 方法 "${apiName}" 不存在，请检查 SDK`)
  }

  const {
    data: res,
    isLoading: loading,
    refetch: reload,
    isFetching,
  } = useQuery<ApiData<TName>>({
    queryKey: [apiName, params, xRbacToken, ...(queryKey || [])],
    queryFn: async () => {
      const token = await getValidToken()
      if (!token) throw new Error('x-rbac-token 缺失，无法请求接口')

      const mergedParams = {
        ...params,
        headers: {
          ...(params as any)?.headers,
          'x-rbac-token': `V1#apisix#${token}`,
        },
      }

      const { data } = await apiFn(mergedParams)

      if (data?.errMsg !== 'success') {
        Toast.error(
          `请求失败：${apiName}：${
            typeof data?.errMsg === 'string'
              ? data?.errMsg?.length > 40
                ? `${data?.errMsg.slice(0, 40)}...`
                : data?.errMsg
              : ''
          } 请联系管理员修复`
        )
        throw new Error(data?.errMsg)
      }

      return data
    },
    enabled,
    ...DEFAULT_QUERY_OPTIONS,
    ...options,
  })

  return {
    data: res ?? null,
    loading,
    reload,
    refetching: enabled && !loading && isFetching,
  }
}
